import yup from '../utils/yup';
import type { AxiosResponse } from 'axios';
import type {
  AccountType,
  APIAccountType,
  DiscoveredAccountDetails,
  LoginProviderEnum,
  Profile,
  ThirdPartyAuthenticationProfileType,
  UserPersistentStateReadType,
  UserPersistentStateUpdateType,
} from 'bc-model';
import api from './api';

type AuthAccountIdentityLinkProperties = {
  accountLinkingEnabled?: boolean;
  linkedIdentityEmail?: string;
  linkedIdentityAt?: string;
};

type AuthAccountWithPassType = AccountType & {
  authorized?: boolean;
  dodConsentRequired?: boolean;
  isProductAdmin?: boolean;
  isSuperAdmin?: boolean;
  isSysAdmin?: boolean;
  id?: number;
  uuid?: string | null;
  sessionStartDate?: number;
  readonly sessionKey?: string;
  readonly password?: string;
  hasPreviousSession?: boolean;
} & AuthAccountIdentityLinkProperties;

type AccountValidationType = ThirdPartyAuthenticationProfileType & {
  error?: string;
  redirect?: string;
  discoveredAccountDetails?: DiscoveredAccountDetails;
};

type UserVerificationType = { loginProvider: LoginProviderEnum } & AccountValidationType;

const accountValidationSchema = yup.object().shape({
  firstName: yup.string().max(60, 'max'),
  lastName: yup.string().max(60, 'max'),
  avatarColor: yup.string().required('required'),
  language: yup.string().nullable(),
  rank: yup.string().nullable(),
  jobTitle: yup.string().nullable(),
  organization: yup.string().nullable(),
  parentOrganization: yup.string().nullable(),
  email: yup.string().email('valid_email').max(255, 'max').required('Required'),
});

function trimUserPayload({
  firstName,
  lastName,
  name,
  id,
  rank,
  jobTitle,
  avatarColor,
  organization,
  parentOrganization,
  email,
  language,
}: AuthAccountWithPassType) {
  return {
    id,
    firstName,
    lastName,
    name,
    rank,
    avatarColor,
    organization,
    parentOrganization,
    jobTitle,
    email,
    language,
  };
}

async function createUser(
  user: AuthAccountWithPassType,
  profile?: Profile
): Promise<AxiosResponse<AuthAccountWithPassType>> {
  return api.post('/api/auth/signup', { ...user, profile });
}

async function createAPIAccount(
  account: APIAccountType,
  organizationId: number
): Promise<AxiosResponse<{ id?: number; uuid?: string; signedJWT: string }>> {
  return api.post(`/api/organizations/${organizationId}/bearer/create`, account);
}

async function updatePassword(
  password: string,
  newPassword: string
): Promise<AxiosResponse<{ error?: 'used' | 'invalid' | undefined }>> {
  return api.post('/api/auth/update', { password, newPassword });
}

async function retrieveUser(): Promise<AxiosResponse<AuthAccountWithPassType>> {
  return api.get('/api/auth/profile');
}

async function retrieveAccount(accountId: number | string): Promise<AxiosResponse<AccountType>> {
  return api.get(`/api/account/${accountId}`);
}

async function searchUsers(
  query: string,
  includeDeleted: boolean = false
): Promise<AxiosResponse<Array<AuthAccountWithPassType>>> {
  return api.get('/api/accounts', { params: { q: query, includeDeleted } });
}

async function updateUser(
  user: AuthAccountWithPassType,
  accountIdentifier?: string | number
): Promise<AxiosResponse<AuthAccountWithPassType>> {
  const userId = accountIdentifier ?? user.id;
  return api.put(`/api/account/${userId}`, trimUserPayload(user));
}

async function forgotPassword(emailAddress: string): Promise<AxiosResponse<{ message: string }>> {
  return api.post('/api/auth/forgot', { name: emailAddress });
}

async function resendVerificationEmail(): Promise<void> {
  await api.post('/api/auth/resendVerificationEmail');
}

async function resetPasswordWithCode(
  code: string,
  password: string
): Promise<AxiosResponse<{ error?: string }>> {
  return api
    .post('/api/auth/reset', { code, password })
    .then((res) => res)
    .catch((error) => error.response);
}

async function verifyAndLinkWithLocalAccount(
  providedId: string,
  emailAddress: string,
  obPassword: string,
  provider: LoginProviderEnum
): Promise<AxiosResponse<UserVerificationType>> {
  return api.post(
    '/api/auth/verifyAndLinkWithLocalAccount',
    { providedId, emailAddress, password: obPassword, provider },
    { validateStatus: (status) => (status >= 200 && status < 300) || status === 429 }
  );
}

async function unlinkIdentityFromLocalAccount(): Promise<AxiosResponse<{ error?: string }>> {
  return api.post('/api/auth/unlinkIdentityFromLocalAccount');
}

async function login(
  email: string,
  password: string,
  queryString: string
): Promise<AxiosResponse<AccountValidationType>> {
  return api.post(`/api/auth/login${queryString}`, { username: email, password });
}

async function doPreLoginCheck(): Promise<
  AxiosResponse<{
    schemas: LoginProviderEnum[];
    cuiDisclaimer: boolean;
    accountValidation: AccountValidationType;
    accountLinking: boolean;
    authenticated?: boolean;
    redirectUrl?: string;
  }>
> {
  return api.get('/api/auth/pre-login');
}

async function getSmtpConfigured(): Promise<AxiosResponse<boolean>> {
  return api.get('/api/auth/smtp');
}

async function confirmDodConsent(): Promise<AxiosResponse<{ redirectUrl?: string }>> {
  return api.post('/api/auth/consent');
}

async function retrieveUserPersistentState(): Promise<
  AxiosResponse<UserPersistentStateReadType[]>
> {
  return api.get(`/api/accounts/persistentState`);
}

async function updateUserPersistentState(
  data: UserPersistentStateUpdateType
): Promise<AxiosResponse<UserPersistentStateReadType>> {
  return api.put(`/api/accounts/persistentState`, data);
}

async function retrieveAccounts(
  accountIds: Array<string | number>
): Promise<AxiosResponse<AccountType[]>> {
  return api.get('/api/accounts', { params: { accountIds } });
}

async function retrieveFavoritePlans(): Promise<AxiosResponse<number[]>> {
  return api.get(`/api/accounts/favoritePlans`);
}

async function addFavoritePlan(planId: number): Promise<AxiosResponse<number[]>> {
  return api.put(`/api/accounts/favoritePlan/${planId}`);
}

async function removeFavoritePlan(planId: number): Promise<AxiosResponse<number[]>> {
  return api.delete(`/api/accounts/favoritePlan/${planId}`);
}

if (typeof window !== 'undefined') {
  (window as any).dev_clientApi = {
    createUser,
    createAPIAccount,
    retrieveUser,
    searchUsers,
    updateUser,
    accountValidationSchema,
    forgotPassword,
    resetPasswordWithCode,
    updatePassword,
    retrieveAccount,
    retrieveAccounts,
    login,
    verifyAndLinkWithLocalAccount,
    getSmtpConfigured,
    doPreLoginCheck,
    confirmDodConsent,
    retrieveUserPersistentState,
    updateUserPersistentState,
    resendVerificationEmail,
    retrieveFavoritePlans,
    addFavoritePlan,
    removeFavoritePlan,
  };
}

export {
  createUser,
  createAPIAccount,
  retrieveUser,
  searchUsers,
  updateUser,
  accountValidationSchema,
  forgotPassword,
  resetPasswordWithCode,
  updatePassword,
  retrieveAccount,
  retrieveAccounts,
  login,
  verifyAndLinkWithLocalAccount,
  unlinkIdentityFromLocalAccount,
  getSmtpConfigured,
  doPreLoginCheck,
  confirmDodConsent,
  retrieveUserPersistentState,
  updateUserPersistentState,
  resendVerificationEmail,
  retrieveFavoritePlans,
  addFavoritePlan,
  removeFavoritePlan,
};
export type { AuthAccountWithPassType, AuthAccountIdentityLinkProperties, AccountValidationType };
